import React, { useCallback, useMemo } from 'react';
import {
  DateField,
  HttpError,
  RecordContextProvider,
  TextField,
  useCreate,
  useDataProvider,
  useGetList,
  useNotify,
  useStore,
} from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { CameraAlt, Cancel } from '@mui/icons-material';
import { useQueries } from '@tanstack/react-query';
import { isNumber } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useScopedEmbeddedViewContext } from '../utils/ScopedEmbeddedViewContext';
import useUpdatedTableFromConfig from '../../hooks/useUpdatedTableFromConfig';
import { useGetConfigurationSummaries } from './TransactionSummaryTable';
import { EntityTypes } from '../../constants/entityTypes';
import { requestTypes } from '../../constants/requestTypes';
import conflictFields from '../../constants/conflictFields';
import { TransactionMonitoringThresholds } from '../../utilities/schemas/datapoints/transactionMonitoringThresholds';
import { Currencies } from '../../constants/currencies';
import useDebounce from '../../hooks/useDebounce';
import Params from './Params';
import transactionTableColumnSources, { TransactionTableColumnSource } from '../../constants/transactionTableColumnSources';
import useDialogStatus from '../../hooks/useDialogStatus';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';

export type TransactionGraphDatapoint = {
  x: string;
  y: number;
  frequency: number;
  currency: Currencies;
};

export type GraphDataSeries = {
  id: string;
  type: 'area';
  name: string;
  data: TransactionGraphDatapoint[];
};

const SnapshotButton = ({
  filter,
  params,
} : {
  params: Params;
  filter: {
    from: string;
    to: string;
    currency: string;
    entityId: string;
    entityType: EntityTypes;
    requestType: typeof requestTypes.TRANSACTION;
  };
}) => {
  const scopedEmbeddedContext = useScopedEmbeddedViewContext();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const {
    typeConfigurations,
  } = useUpdatedTableFromConfig({ preferenceKey: 'transaction-summary-table' });

  const tableData = useGetConfigurationSummaries(typeConfigurations, filter, false);
  const isLoadingTableData = tableData.some((data) => data.isLoading);

  const notify = useNotify();

  const dataProvider = useDataProvider();
  const conflictData = useQueries({
    queries: typeConfigurations.map((configuration) => {
      const id = `${conflictFields.TRANSACTION_MONITORING_THRESHOLD}/${filter.entityId}/${filter.entityType}${configuration ? `/${JSON.stringify(configuration)}` : ''}`;
      return {
        queryKey: [
          'data-points/status',
          'getOne',
          {
            id,
          }],
        queryFn: async () => {
          const response = await dataProvider.getOne<{
            id: string;
            status: 'resolved' | 'conflict' | 'empty';
            selectedValue: TransactionMonitoringThresholds | undefined;
          }>('requests/graph/stats', { id });
          return {
            ...response.data,
            id,
          };
        },
        onError: (e: unknown) => {
          notify(e instanceof HttpError && e.status === 400 ? e.message : 'Cannot fetch transaction summary', { type: 'error' });
        },
      };
    }),
  });
  const isLoadingConflictData = conflictData.some((data) => data.isLoading);

  const { data: dailySumData, isLoading: isLoadingDailySumData } = useGetList<GraphDataSeries>(
    'requests/graph/daily-sum',
    {
      filter,
    },
    {
      onError: (e) => {
        notify(e instanceof HttpError && e.status === 400 ? e.message : 'Cannot fetch transaction stats', { type: 'error' });
      },
    },
  );

  const debouncedWindowSize = useDebounce(params.rollingSumWindow, 400);
  const { data: rollingSumData, isLoading: isLoadingRollingSum } = useGetList<GraphDataSeries>(
    'requests/graph/rolling-sum',
    {
      filter: { ...filter, windowSize: debouncedWindowSize },
    },
    {
      enabled: isNumber(params.rollingSumWindow),
      onError: (e) => {
        notify(e instanceof HttpError && e.status === 400 ? e.message : 'Cannot fetch transaction stats', { type: 'error' });
      },
    },
  );

  const [createSnapshot] = useCreate();
  const [expectedBehaviour] = useStore('transactionShowExpectedBehavior', true);

  const behaviourData = useQueries({
    queries: typeConfigurations.map((configuration) => {
      const identificationInfo = configuration ? {
        transactionTypes: configuration,
      } : undefined;
      const id = `${conflictFields.TRANSACTION_MONITORING_THRESHOLD}/${filter.entityId}/${filter.entityType}${identificationInfo ? `/${JSON.stringify(identificationInfo)}` : ''}`;
      return {
        queryKey: [
          'data-points/values',
          'getOne',
          {
            id,
          }],
        queryFn: async () => {
          const { data } = await dataProvider.getOne<{
            id: string;
            status: 'resolved' | 'conflict' | 'empty';
            value: {
              createdAt: string;
              value: TransactionMonitoringThresholds;
            } | undefined;
            values: {
              createdAt: string;
              value: TransactionMonitoringThresholds;
            }[] | undefined;
          }>('data-points/values', {
            id,
          });
          return data;
        },
        onError: (e: unknown) => {
          notify(e instanceof HttpError && e.status === 400 ? e.message : 'Cannot fetch transaction summary', { type: 'error' });
        },
      };
    }),
  });

  const isLoadingBehaviourData = behaviourData.some((data) => data.isLoading);
  const [currentColumns] = useStore<string[]>('preferences.transaction-summary-table.columns', []);
  const [availableColumns] = useStore<{
    index: string;
    source: TransactionTableColumnSource;
    label: string;
  }[]>('preferences.transaction-summary-table.availableColumns');

  const transactionTableColumns = useMemo(() => {
    if (
      !availableColumns
      || !availableColumns.length
    ) return [];
    return availableColumns.map(({ index, source }) => ({
      source,
      enabled: !currentColumns.length || currentColumns.includes(index),
    }));
  }, [availableColumns, currentColumns]);

  const caseId = scopedEmbeddedContext.isScopedEmbedded
    ? scopedEmbeddedContext.scopedParentRecordId : undefined;

  const { getValues } = useFormContext();
  const onSnapshot = useCallback(() => {
    const data = {
      entityId: params.entityId,
      entityType: params.entityType,
      from: params.from,
      to: params.to,
      currency: params.currency,
      showExpectedBehaviour: expectedBehaviour,
      expectedBehaviour: behaviourData.map(({ data: behaviorData }, index) => [
        ...behaviorData && behaviorData.value ? [behaviorData.value] : [],
        ...behaviorData && behaviorData.values ? behaviorData.values : [],
      ].map((behaviour) => ({
        transactionTypeConfiguration: typeConfigurations[index],
        createdAt: behaviour.createdAt,
        value: behaviour.value,
      }))).flat(),
      window: params.rollingSumWindow,
      transactionTable: {
        tableData: tableData.map((row, index) => ({
          transactionTypeConfiguration: typeConfigurations[index],
          data: {
            ...row.data,
            conflictStatus: conflictData[index].data?.status ?? 'empty',
          },
        })),
        tableColumns: transactionTableColumns,
        rowToShowInGraph: getValues(transactionTableColumnSources.SELECTED_ROW),
      },
      dailySumGraphData: dailySumData,
      rollingSumGraphData: rollingSumData,
    };
    closeDialog();
    createSnapshot(`cases/${caseId}/transaction-snapshot`, {
      data,
    }, {
      onSuccess: () => notify('Snapshot created', { type: 'info' }),
      onError: () => notify('Could not create snapshot.', { type: 'error' }),
    });
  }, [
    closeDialog,
    params.entityId,
    params.entityType,
    params.from,
    params.to,
    params.currency,
    params.rollingSumWindow,
    expectedBehaviour,
    behaviourData,
    tableData,
    transactionTableColumns,
    getValues,
    dailySumData,
    rollingSumData,
    createSnapshot,
    caseId,
    typeConfigurations,
    conflictData,
    notify,
  ]);

  if (!caseId
    || !scopedEmbeddedContext.isScopedEmbedded
    || isLoadingTableData
    || isLoadingConflictData
    || isLoadingDailySumData
    || isLoadingBehaviourData
    || isLoadingRollingSum
  ) return null;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CameraAlt />}
        onClick={openDialog}
      >
        Snapshot
      </Button>
      <Dialog open={open} onClose={closeDialog} maxWidth="md">
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" fontWeight="bold">
              Create a Snapshot
            </Typography>
            <IconButton onClick={closeDialog}>
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You are about to create a snapshot of the transaction data based on
            your selected filters. The snapshot will be attached to this case and
            can later be accessed from the case view.
          </Typography>
          <RecordContextProvider value={filter}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" flexDirection="row" gap={1} marginTop={5}>
                <Typography variant="description" fontStyle="normal">Currency:</Typography>
                <TextField source="currency" />
              </Box>
              <Box display="flex" flexDirection="row" gap={1}>
                <Typography variant="description" fontStyle="normal">Date:</Typography>
                <DateField source="from" />
                <Typography>-</Typography>
                <DateField source="to" />
              </Box>
              <Box display="flex" flexDirection="row" gap={1}>
                <Typography variant="description" fontStyle="normal">Entity:</Typography>
                <ReferenceEntityField source="" idKey="entityId" typeKey="entityType" linkType={false}>
                  <EntityDisplayNameField />
                </ReferenceEntityField>
              </Box>
            </Box>
          </RecordContextProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button type="submit" variant="contained" onClick={onSnapshot}>Create Snapshot</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SnapshotButton;
